import { AnimatePresence, motion } from "motion/react";
import {
    Children,
    type ReactNode,
    type RefObject,
    useEffect,
    useEffectEvent,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import { cn } from "../../lib/cn";
import {
    ActiveInputActions,
    ExpandInputAction,
    InactiveInputActions,
    VoiceRecordingActions,
} from "./input/Actions";
import { ChatInputProvider, useChatInputContext } from "./input/Context";
import { chatInputMotionTransition } from "./input/motion";
import { useChatInput } from "./input/useChatInput";
import { useVoiceRecording } from "./input/useVoiceRecording";
import { type ChatInputLabels, defaultChatInputLabels } from "./labels";

const chatInputTextareaClass = cn(
    "m-0 box-border w-full min-w-0 flex-1 resize-none whitespace-pre-wrap rounded-rounded border-none bg-transparent p-16 font-normal outline-none",
    "font-paragraph-base font-weight-paragraph-regular text-mobile-chat-medium leading-mobile-chat-medium",
    "placeholder:text-text-tertiary md:text-desktop-chat-medium md:leading-desktop-chat-medium",
    "transition-[height,min-height,max-height] duration-150 ease-out",
    "[transition-timing-function:cubic-bezier(0.59,0.15,0.4,0.78)]",
);

const collapsedTextareaClass = "max-h-64 h-56 min-h-56";

const expandedTextareaClass = cn(
    "h-[calc(55svh-var(--spacing-48))] max-h-[calc(55svh-var(--spacing-48))] min-h-0",
    "md:h-[calc(70svh-var(--spacing-48))] md:max-h-[calc(70svh-var(--spacing-48))]",
);

const contextCollapseVariants = {
    hidden: {
        height: 0,
        opacity: 0,
    },
    visible: {
        height: "auto",
        opacity: 1,
    },
};

const chatInputChromeBaseClass = cn(
    "flex min-h-0 w-full flex-col items-start border-[0.5px] md:my-12 md:self-center",
    "transition-[background-color,backdrop-filter,border-color,margin] duration-300",
    "[transition-timing-function:cubic-bezier(0.59,0.15,0.4,0.78)]",
);

const inactiveChatInputChromeClass = cn(
    "border-white bg-component-navigation-bottom-container",
    "backdrop-blur-md",
);

const activeChatInputChromeClass =
    "mb-0 border-transparent bg-universal-white backdrop-blur-none";

const MD_MEDIA_QUERY = "(min-width: 768px)";

type ChatInputProps = {
    acceptedFileTypes?: readonly string[];
    canSendEmpty?: boolean;
    context?: ReactNode;
    label: string;
    labels?: ChatInputLabels;
    collapseGeneration?: number;
    initialActive?: boolean;
    initialExpanded?: boolean;
    initialValue?: string;
    onActiveChange?: (isActive: boolean) => void;
    onFileSelected?: (file: File) => void;
    onRecordingChange?: (isRecording: boolean) => void;
    onRecordingError?: (error: Error) => void;
    onSend: (message: string) => void;
    onSendVoice?: (file: File) => void;
    placeholder: string;
};

export function ChatInput({
    acceptedFileTypes,
    canSendEmpty = false,
    collapseGeneration = 0,
    context,
    label,
    labels = defaultChatInputLabels,
    initialActive = false,
    initialExpanded = false,
    initialValue = "",
    onActiveChange,
    onFileSelected,
    onRecordingChange,
    onRecordingError,
    onSend,
    onSendVoice,
    placeholder,
}: ChatInputProps) {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const inputId = `chat-input${useId().replaceAll(":", "")}`;
    const chromeRef = useRef<HTMLDivElement>(null);
    const hasMeasuredPillRadiusRef = useRef(false);
    const [pillRadius, setPillRadius] = useState("28px");
    const isMd = useIsMdBreakpoint();
    const voiceRecording = useVoiceRecording({
        labels,
        onRecordingChange,
        onRecordingError,
        onSendVoice,
    });
    const chatInput = useChatInput({
        acceptedFileTypes,
        canSendEmpty,
        collapseGeneration,
        initialActive,
        initialExpanded,
        initialValue,
        inputContainerRef: chromeRef,
        inputRef,
        isRecording: voiceRecording.isRecording,
        onFileSelected,
        onSend,
    });
    const isChromeExpanded = chatInput.isActive || voiceRecording.isRecording;
    const showContext =
        chatInput.isActive &&
        !voiceRecording.isRecording &&
        hasRenderableContext(context);

    const notifyActiveChange = useEffectEvent((isActive: boolean) => {
        onActiveChange?.(isActive);
    });

    useEffect(
        function notifyActiveChangeOnChange() {
            notifyActiveChange(chatInput.isActive);
        },
        [chatInput.isActive],
    );

    useLayoutEffect(
        function syncInactivePillRadius() {
            if (!chromeRef.current || isChromeExpanded) {
                return;
            }

            function syncPillRadius() {
                const node = chromeRef.current;
                if (!node) {
                    return;
                }

                setPillRadius(`${node.offsetHeight / 2}px`);
                hasMeasuredPillRadiusRef.current = true;
            }

            if (!hasMeasuredPillRadiusRef.current) {
                syncPillRadius();
                return;
            }

            const timeoutId = window.setTimeout(
                syncPillRadius,
                chatInputMotionTransition.duration * 1000,
            );
            return function clearPillRadiusTimeout() {
                window.clearTimeout(timeoutId);
            };
        },
        [isChromeExpanded],
    );

    const radiusRounded = readRadiusRounded();
    const borderRadius = isChromeExpanded
        ? isMd
            ? cornerRadii(radiusRounded)
            : topCornerRadii(radiusRounded)
        : cornerRadii(pillRadius);

    return (
        <ChatInputProvider
            value={{
                ...chatInput,
                cancelRecording: voiceRecording.cancelRecording,
                elapsedMs: voiceRecording.elapsedMs,
                isRecording: voiceRecording.isRecording,
                labels,
                onSendVoice,
                sendRecording: voiceRecording.sendRecording,
                startRecording: voiceRecording.startRecording,
            }}
        >
            <motion.div
                ref={chromeRef}
                initial={false}
                animate={{ borderRadius }}
                transition={chatInputMotionTransition}
                onClick={chatInput.handleChatInputPress}
                className={cn(
                    chatInputChromeBaseClass,
                    chatInput.isExpanded ? "mt-12" : undefined,
                    isChromeExpanded
                        ? activeChatInputChromeClass
                        : inactiveChatInputChromeClass,
                    // Active chrome is a sheet that meets the screen edge. Keep the
                    // home-indicator inset inside that sheet until the keyboard is
                    // actually open — `isActive` alone is also used for tagged context.
                    isChromeExpanded &&
                        !chatInput.isMobileKeyboardOpen &&
                        "pointer-coarse:pb-[var(--safe-area-inset-bottom)]",
                )}
            >
                <AnimatePresence initial={false}>
                    {showContext ? (
                        <motion.div
                            key="chat-input-context"
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            variants={contextCollapseVariants}
                            transition={chatInputMotionTransition}
                            className="w-full shrink-0 overflow-hidden"
                        >
                            <div className="w-full pb-8">{context}</div>
                        </motion.div>
                    ) : null}
                </AnimatePresence>

                <div className="w-full">
                    <div hidden={voiceRecording.isRecording}>
                        <ChatInputEditor
                            inputId={inputId}
                            label={label}
                            inputRef={inputRef}
                            placeholder={placeholder}
                        />
                    </div>
                    {voiceRecording.isRecording ? <VoiceRecordingActions /> : null}
                </div>
            </motion.div>
        </ChatInputProvider>
    );
}

type ChatInputEditorProps = {
    inputId: string;
    label: string;
    inputRef: RefObject<HTMLTextAreaElement | null>;
    placeholder: string;
};

function ChatInputEditor({
    inputId,
    label,
    inputRef,
    placeholder,
}: ChatInputEditorProps) {
    const chatInput = useChatInputContext();

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col items-start">
            <div className="relative flex min-h-0 w-full flex-1 flex-row items-start justify-between gap-8">
                <textarea
                    key={chatInput.fieldEpoch}
                    id={inputId}
                    ref={inputRef}
                    aria-label={label}
                    placeholder={placeholder}
                    rows={1}
                    value={chatInput.input}
                    className={cn(
                        chatInputTextareaClass,
                        chatInput.isExpanded
                            ? expandedTextareaClass
                            : collapsedTextareaClass,
                        !chatInput.isActive &&
                            "pointer-events-none absolute inset-0 opacity-0",
                    )}
                    onPointerDown={chatInput.handleTextareaPointerDown}
                    onFocus={chatInput.activate}
                    onBlur={chatInput.handleBlur}
                    onChange={chatInput.handleChange}
                    onKeyDown={chatInput.handleKeyDown}
                />

                {!chatInput.isActive ? (
                    <div
                        aria-hidden="true"
                        className="pointer-events-none box-border flex h-56 min-w-0 flex-1 items-center p-16"
                    >
                        <span
                            className={cn(
                                "block w-full min-w-0 truncate font-paragraph-base font-weight-paragraph-regular text-mobile-chat-medium leading-mobile-chat-medium md:text-desktop-chat-medium md:leading-desktop-chat-medium",
                                chatInput.input
                                    ? "text-text-primary"
                                    : "text-text-tertiary",
                            )}
                        >
                            {chatInput.input || placeholder}
                        </span>
                    </div>
                ) : null}

                {!chatInput.isActive ? <InactiveInputActions /> : null}

                {chatInput.isActive && chatInput.isExpandable ? (
                    <ExpandInputAction />
                ) : null}
            </div>

            <ActiveInputActions />
        </div>
    );
}

function hasRenderableContext(context: ReactNode): boolean {
    return Children.count(Children.toArray(context)) > 0;
}

function useIsMdBreakpoint() {
    return useSyncExternalStore(
        subscribeMdBreakpoint,
        getMdBreakpointSnapshot,
        getMdBreakpointServerSnapshot,
    );
}

function subscribeMdBreakpoint(onStoreChange: () => void) {
    const mediaQuery = window.matchMedia(MD_MEDIA_QUERY);
    mediaQuery.addEventListener("change", onStoreChange);
    return () => mediaQuery.removeEventListener("change", onStoreChange);
}

function getMdBreakpointSnapshot() {
    return window.matchMedia(MD_MEDIA_QUERY).matches;
}

function getMdBreakpointServerSnapshot() {
    return false;
}

function readRadiusRounded() {
    if (typeof document === "undefined") {
        return "12px";
    }

    return (
        getComputedStyle(document.documentElement)
            .getPropertyValue("--radius-rounded")
            .trim() || "12px"
    );
}

function cornerRadii(radius: string) {
    return `${radius} ${radius} ${radius} ${radius}`;
}

function topCornerRadii(radius: string) {
    return `${radius} ${radius} 0px 0px`;
}
